import { Typography } from '@mui/material';
import { get, isEqual } from 'lodash';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import { Footer } from 'src/components/footer/Footer';
import { PublicationModal, SimpleDialog } from 'src/components/modal';
import { Progress } from 'src/components/progress';
import { Snackbar } from 'src/components/snackbar';
import {
  IDaypartRecord,
  IScheduleWeekdayDaypartRecord,
  TimeRow,
} from 'src/components/timeRow';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { handleGetStartTime } from 'src/features/locations/timingLocationGroupPage/modules/locationGroupTimingsGrid/LocationGroupTimingsGrid';
import { generateWeekdayDaypartRecords } from 'src/features/locations/timingLocationGroupPage/modules/locationGroupTimingsGrid/LocationGroupTimingsGrid.helper';
import {
  scheduleAssembliesTransaction,
  scheduleDaypartOverrideTransaction,
  useCreateOrUpdateScheduleDaypartOverrides,
  useGetAllDayparts,
  useGetLocation,
  useGetScheduleDaypartOverrides,
  useGetScheduleDayparts,
  useGetWeekdayDayparts,
  useModal,
} from 'src/hooks';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { useGetDefaultAssemblyOfDayparts } from 'src/hooks/useGetDefaultAssemblyOfDayparts';
import { useGetPageParamsString } from 'src/hooks/useGetPageParamsString';
import {
  IAssembly,
  IDaypart,
  ILocationGroup,
  IScheduleDaypart,
} from 'src/interfaces';
import { lang, useUserRole } from 'src/utils';
import { locationEditTimingsEvent } from 'src/utils/google-tag-manager';

import {
  checkDaypartsStatus,
  getGroupDaypartActiveStatus,
  mapScheduleWeekdayDaypartsRecord,
} from './LocationTimingsGrid.helper';
import { Container } from './LocationTimingsGrid.styled';

export const LocationTimingsGrid = (props: {
  locationGroupData: ILocationGroup;
  locGroupScheduleDayparts: IScheduleDaypart[];
  isDefaultLocationGroup: boolean;
  rbStoreIntegration: boolean;
}) => {
  const { userRole } = useContext(AuthDataProvider);
  const { isViewOnly, isOwnerOperator, getLocationsPath } = useUserRole();

  const { locationCode = '' } = useParams();
  const locationGroupId = props.locationGroupData?.id;
  const navigate = useNavigate();
  const searchString = useGetPageParamsString();
  const saveModal = useModal();
  const publishModal = useModal();

  const [daypartsRecord, setDaypartsRecord] = useState<IDaypartRecord[]>([]);
  const [scheduleWeekdayDaypartsRecord, setScheduleWeekdayDaypartsRecord] =
    useState<IScheduleWeekdayDaypartRecord[]>();
  const updateStoreTimes = useCreateOrUpdateScheduleDaypartOverrides();

  const { isLoading: isLoadingDayparts, error: errorLoadingDayparts } =
    useGetAllDayparts();

  const { data: weekdayDaypartOptions, isLoading: isLoadingWeekdayDayparts } =
    useGetWeekdayDayparts();
  const { data: locationDaypartsOverrides } = useGetScheduleDaypartOverrides([
    `location_code=${locationCode}`,
  ]);
  const { data: location } = useGetLocation(locationCode, userRole);

  const enableScheduleDayparts = !props.rbStoreIntegration
    ? Boolean(locationGroupId)
    : !props.rbStoreIntegration;

  const { data: locGroupDayparts } = useGetScheduleDayparts(
    [`location_group_id=${locationGroupId}`],
    enableScheduleDayparts,
  );

  const { daypartsWithDefaultAssembly: daypartsWithDefaultAssemblyGroup } =
    useGetDefaultAssemblyOfDayparts(
      String(locationGroupId),
      props.rbStoreIntegration,
    );

  const groupScheduleWeekdayDaypartsRecord = useMemo(
    () =>
      !props.rbStoreIntegration
        ? generateWeekdayDaypartRecords({
            weekdayDaypartOptions,
            locGroupDayparts,
            locationGroupId,
          })
        : [],
    [
      locGroupDayparts,
      locationGroupId,
      props.rbStoreIntegration,
      weekdayDaypartOptions,
    ],
  );

  const groupDaypartsActiveStatus = useMemo(
    () =>
      getGroupDaypartActiveStatus(
        daypartsWithDefaultAssemblyGroup,
        groupScheduleWeekdayDaypartsRecord,
        props.rbStoreIntegration,
      ),
    [
      daypartsWithDefaultAssemblyGroup,
      groupScheduleWeekdayDaypartsRecord,
      props.rbStoreIntegration,
    ],
  );

  const originalScheduleWeekdayDaypartsRecord = useMemo(
    () =>
      mapScheduleWeekdayDaypartsRecord(
        locationGroupId,
        props.locGroupScheduleDayparts,
        locationDaypartsOverrides,
        weekdayDaypartOptions,
      ),
    [
      locationDaypartsOverrides,
      locationGroupId,
      props.locGroupScheduleDayparts,
      weekdayDaypartOptions,
    ],
  );

  useEffect(() => {
    setScheduleWeekdayDaypartsRecord(originalScheduleWeekdayDaypartsRecord);
  }, [originalScheduleWeekdayDaypartsRecord]);

  useEffect(() => {
    setDaypartsRecord(daypartsWithDefaultAssemblyGroup);
  }, [daypartsWithDefaultAssemblyGroup]);

  const onChangeAssembly = (daypart_id: number, assembly: IAssembly) => {
    const updatedDaypartAssembly = daypartsRecord?.map((item) => {
      if (item.id === daypart_id) {
        const defaultAssembly = item.defaultAssembly
          ? [...item.defaultAssembly]
          : [];
        const existingIndex = defaultAssembly.findIndex(
          (asm) => asm.id === assembly.id,
        );

        if (existingIndex !== -1) {
          defaultAssembly[existingIndex] = assembly;
        } else {
          defaultAssembly.push(assembly);
        }

        return {
          ...item,
          defaultAssembly,
        };
      } else return item;
    });
    setDaypartsRecord(updatedDaypartAssembly);
  };

  const onHandleSave = async () => {
    try {
      const daypartsActiveStatus = checkDaypartsStatus(
        scheduleWeekdayDaypartsRecord ?? [],
      );
      if (
        daypartsRecord?.some(
          (daypart) =>
            daypartsActiveStatus.get(daypart.id) &&
            !daypart.defaultAssembly?.length,
        )
      ) {
        Snackbar.error('All enabled dayparts must have a default assembly');
      } else {
        let overriddenDayparts =
          scheduleWeekdayDaypartsRecord?.filter((scheduleDaypart) => {
            const originalDaypart = originalScheduleWeekdayDaypartsRecord?.find(
              (groupDaypart) => groupDaypart.id === scheduleDaypart.id,
            );

            return (
              originalDaypart &&
              originalDaypart.start_time !== scheduleDaypart.start_time
            );
          }) ?? [];

        if (isOwnerOperator) {
          overriddenDayparts = overriddenDayparts?.filter(
            (override) => override.start_time !== null,
          );
        }
        let dayparts: IDaypart[] = [];
        let location_id: number | undefined;
        let payload:
          | scheduleAssembliesTransaction[]
          | scheduleDaypartOverrideTransaction[] = overriddenDayparts.map(
          (scheduleDaypart) => {
            dayparts.push(get(scheduleDaypart, 'daypart'));
            location_id = scheduleDaypart.id;
            const startTime = handleGetStartTime(
              get(scheduleDaypart, 'start_time'),
            );

            return {
              resource: COLLECTIONS.SCHEDULE_DAYPART_OVERRIDES,
              method: scheduleDaypart?.scheduleDaypartOverrideId
                ? 'PATCH'
                : 'POST',
              id: get(scheduleDaypart, 'scheduleDaypartOverrideId'),
              data: scheduleDaypart?.scheduleDaypartOverrideId
                ? {
                    start_time: startTime,
                    narrative: narrativeGenerators[
                      COLLECTIONS.SCHEDULE_DAYPART_OVERRIDES
                    ][USER_ACTIONS.UPDATE](
                      scheduleDaypart.daypart.name,
                      scheduleDaypart.weekday.name,
                      location?.hq?.name ?? '',
                      locationCode,
                    ),
                  }
                : {
                    schedule_daypart_id: get(scheduleDaypart, 'id'),
                    location_code: locationCode,
                    start_time: startTime,
                    narrative: narrativeGenerators[
                      COLLECTIONS.SCHEDULE_DAYPART_OVERRIDES
                    ][USER_ACTIONS.CREATE](
                      scheduleDaypart.daypart.name,
                      scheduleDaypart.weekday.name,
                      location?.hq?.name ?? '',
                      locationCode,
                    ),
                  },
            };
          },
        );

        await updateStoreTimes.mutateAsync(payload);
        locationEditTimingsEvent(
          location_id,
          dayparts,
          props.locationGroupData.channel_name,
        );
        saveModal.onCloseModal();
        publishModal.onOpenModal();
      }
    } catch {
      Snackbar.error(lang.failedSaveText);
    }
  };

  const handleChangeScheduleDayparts = (
    scheduleDayparts: IScheduleWeekdayDaypartRecord[],
  ) => {
    const updatedMap = new Map(
      scheduleDayparts.map((s) => [s.weekday_daypart_id, s]),
    );

    setScheduleWeekdayDaypartsRecord((prev) => {
      if (!prev) return prev;

      return prev.map(
        (item) => updatedMap.get(item.weekday_daypart_id) || item,
      );
    });
  };

  const anyDaypartsUpdate = !isEqual(
    originalScheduleWeekdayDaypartsRecord,
    scheduleWeekdayDaypartsRecord,
  );

  const enabledSave =
    !props.rbStoreIntegration &&
    anyDaypartsUpdate &&
    scheduleWeekdayDaypartsRecord?.some(
      (scheduleDaypart) => scheduleDaypart.start_time,
    );

  return (
    <Container>
      {isLoadingDayparts || isLoadingWeekdayDayparts ? (
        <Progress />
      ) : errorLoadingDayparts ? (
        <Typography>{lang.errorOccurred}</Typography>
      ) : (
        <>
          {props.locationGroupData &&
            daypartsRecord?.map((dayPart, index) => {
              const scheduleDayparts = scheduleWeekdayDaypartsRecord?.filter(
                (weekday) => weekday?.daypart?.id === dayPart.id,
              );
              const originalScheduleDayparts =
                originalScheduleWeekdayDaypartsRecord.filter(
                  (weekday) => weekday?.daypart?.id === dayPart.id,
                );

              return (
                <TimeRow
                  key={index}
                  locationGroup={props.locationGroupData}
                  daypartRecord={dayPart}
                  originalScheduledDayparts={originalScheduleDayparts}
                  scheduledDayparts={scheduleDayparts}
                  scheduleWeekdayDaypartsRecord={scheduleWeekdayDaypartsRecord}
                  setDefaultAssembly={onChangeAssembly}
                  isDefaultLocationGroup={props.isDefaultLocationGroup}
                  rbStoreIntegration={props.rbStoreIntegration}
                  isLocationGroupPage={false}
                  groupDaypartsActiveStatus={groupDaypartsActiveStatus}
                  handleChangeScheduleDayparts={handleChangeScheduleDayparts}
                  options={{
                    canAddAssembly: false,
                    canEditAssembly: false,
                    canScheduleAssembly: false,
                  }}
                  setScheduleAssembly={() => {
                    // TODO Add logic to add schedule assembly
                  }}
                />
              );
            })}
        </>
      )}
      <SimpleDialog
        open={saveModal.open}
        cancelText={lang.cancel}
        confirmText={lang.confirm}
        title={lang.titleTextSave}
        subTitle={lang.subTitlePublishConfirm}
        onClose={saveModal.onCloseModal}
        onClickCancel={saveModal.onCloseModal}
        modalSize="sm"
        onClickConfirm={onHandleSave}
        isLoading={updateStoreTimes.isLoading}
      />

      {publishModal.open && (
        <PublicationModal
          open={publishModal.open}
          locationCodes={[locationCode]}
          onClose={publishModal.onCloseModal}
        />
      )}

      {!isViewOnly && (
        <Footer
          showFooterActions
          hasSubNav
          onCancel={() =>
            navigate({ pathname: getLocationsPath(), search: searchString })
          }
          onSave={saveModal.onOpenModal}
          disableSave={!enabledSave}
        />
      )}
    </Container>
  );
};
